import { useEffect, useRef } from 'react';
import { previewPixels } from '../app/adjust';
import { layerNaturalSize } from '../core/model/bounds';
import { blendModeToComposite } from '../core/model/compositing';
import { getLayer } from '../core/model/document';
import { activeMask, canHaveMask } from '../core/model/mask';
import { loadMaskCoverage } from '../io/mask';
import { useEditorStore } from '../state/editorStore';

/**
 * Live preview of an adjustment, drawn over the layer it applies to.
 *
 * The canvas backing store is the layer's own pixel size and CSS scales it, so a
 * slider tick costs a `putImageData` rather than a re-encoded PNG. Layer opacity,
 * blend, rotation and mask are all mirrored — opacity and blend in CSS, the mask
 * composited with the same `destination-in` rule the engine bakes (through the
 * same `io/mask` code) and rotation as a CSS transform about the box centre —
 * so the preview is not a lie about the result on any layer the tool accepts.
 *
 * Coordinates are **stage-relative**: this element lives inside the stage, which is
 * the `position: relative` containing block and clips its overflow. Adding the
 * stage's own page offset on top would shift the preview by exactly that much and
 * slide most of it out of view.
 */
export function AdjustPreview() {
  const adjustment = useEditorStore((s) => s.adjustment);
  const viewport = useEditorStore((s) => s.viewport);
  const doc = useEditorStore((s) => s.doc);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // A mask's coverage canvas, keyed by everything that feeds one. Slider ticks
  // redraw the pixels but not the mask, and inverting a large mask per tick
  // would be a very expensive way to watch a slider lag.
  const coverageRef = useRef<Map<string, HTMLCanvasElement>>(new Map());

  const layer = adjustment ? getLayer(doc, adjustment.layerId) : undefined;
  const kind = adjustment?.kind;
  const params = adjustment?.params;
  const layerId = adjustment?.layerId;
  const ready = adjustment?.ready ?? false;
  const mask = canHaveMask(layer) ? activeMask(layer) : null;
  const maskSrc = mask?.src;
  const maskInverted = mask?.inverted ?? false;

  useEffect(() => {
    // The layer is part of the request: switching which layer is previewed must
    // redraw even when the parameters happen to match. `ready` is here because
    // the pixels arrive after the panel has already switched over.
    if (!kind || !params || !layerId || !ready) return;
    const pixels = previewPixels(kind, params);
    const canvas = canvasRef.current;
    if (!pixels || !canvas) return;
    if (canvas.width !== pixels.width || canvas.height !== pixels.height) {
      canvas.width = pixels.width;
      canvas.height = pixels.height;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const image = ctx.createImageData(pixels.width, pixels.height);
    image.data.set(pixels.data);
    ctx.putImageData(image, 0, 0);

    // Then the mask — what the scene shows of this layer, not the raw pixels
    // the slider filtered. A missing mask leaves the pixels as they are.
    if (!maskSrc) return;
    const cacheKey = `${maskSrc}|${maskInverted}`;
    let cancelled = false;
    void (async () => {
      let coverage = coverageRef.current.get(cacheKey);
      if (!coverage) {
        try {
          coverage = await loadMaskCoverage(maskSrc, maskInverted);
        } catch {
          return; // The scene shows an undecodable mask as nothing; so do we.
        }
        coverageRef.current.set(cacheKey, coverage);
      }
      if (cancelled) return;
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(coverage, 0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, params, layerId, ready, maskSrc, maskInverted]);

  if (!adjustment || !layer || !ready) return null;

  // The layer's own raster size — schema v3 carries it, so a cropped paint
  // layer previews at its real extent instead of the document's.
  const intrinsic = layerNaturalSize(layer);
  if (!intrinsic) return null;

  return (
    <canvas
      ref={canvasRef}
      data-testid="adjust-preview"
      className="pointer-events-none absolute"
      style={{
        left: layer.x * viewport.zoom + viewport.pan.x,
        top: layer.y * viewport.zoom + viewport.pan.y,
        width: `${intrinsic.width * layer.scaleX * viewport.zoom}px`,
        height: `${intrinsic.height * layer.scaleY * viewport.zoom}px`,
        opacity: layer.opacity,
        mixBlendMode: blendModeToComposite(layer.blendMode) as never,
        // The scene rotates about the layer's centre; so does a CSS transform
        // with its default origin, which is this box's centre.
        transform: `rotate(${layer.angle}deg)`,
      }}
    />
  );
}
